import { Controller } from "@hotwired/stimulus"

// Wipes text in one line at a time.
//
// Line breaks depend on layout, so the server renders the plain string and
// this measures it: words are appended one by one and a height increase
// means a new line began. Each line is then wrapped in its own span with a
// staggered delay, and the wipe runs once the element is in view. Under
// reduced motion the text is left exactly as rendered.
export default class extends Controller {
  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const text = this.element.textContent.trim()
    if (!text) return

    this.lines = []
    this.wrapLines(text)
    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        this.start()
        this.observer.disconnect()
      }
    }, { threshold: 1, rootMargin: "0px 0px -10% 0px" })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  wrapLines(text) {
    const element = this.element
    const originalColor = element.style.color
    element.style.color = "transparent"
    element.textContent = ""

    const lines = []
    let previousHeight = 0
    for (const word of text.split(" ")) {
      element.textContent += `${word} `
      const height = element.offsetHeight
      if (lines.length === 0 || height > previousHeight) {
        lines.push([word])
      } else {
        lines[lines.length - 1].push(word)
      }
      previousHeight = height
    }

    element.style.color = originalColor
    element.textContent = ""
    lines.forEach((words, i) => {
      const span = document.createElement("span")
      span.className = "TextRevealAnimation"
      span.style.setProperty("--animation-delay", `${220 + i * 200}ms`)
      span.textContent = words.join(" ")
      // The trailing space keeps the last word of one line and the first of
      // the next from reading as a single word.
      element.append(span, " ")
      this.lines.push(span)
    })
  }

  start() {
    const last = this.lines[this.lines.length - 1]
    for (const line of this.lines) {
      line.classList.add("TextRevealAnimation--visible", "TextRevealAnimation--animated")
    }
    last?.addEventListener("animationend", () => {
      for (const line of this.lines) line.classList.remove("TextRevealAnimation--animated")
    }, { once: true })
  }
}
